import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { FadeIn } from "@/components/FadeIn";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const workAreas = [
  {
    title: "Self-Inquiry & Inner Transformation",
    desc: "Inquiry into the psychological structure of the 'I', fear, desire, conditioning, and conflict. Exploring whether awareness can end mechanical reactions.",
  },
  {
    title: "Alternative Education",
    desc: "Exploring learning beyond conditioning. Can education liberate rather than condition? Can it nurture love, compassion, and non-fragmented human beings?",
  },
  {
    title: "Earth & Ecology",
    desc: "Engagement with climate change, regenerative economy, and natural farming. Questioning humanity's relationship with the finite earth.",
  },
  {
    title: "Social Entrepreneurship",
    desc: "Social Entrepreneurship as a goodness for welfare of the world, focusing on localisation, and solving critical problems through creativity and innovation.",
  },
];

export default function HomePage() {
  const [upcomingEvent, setUpcomingEvent] = useState(null);
  const [videos, setVideos] = useState([]);
  const [journals, setJournals] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events?status=upcoming`).then((r) => {
      if (r.data.length > 0) setUpcomingEvent(r.data[0]);
    }).catch(() => {});
    axios.get(`${API}/videos`).then((r) => setVideos(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/journals`).then((r) => setJournals(r.data.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ── Opening Inquiry ── */}
      <section className="min-h-[80vh] flex items-center justify-center px-6 md:px-12 lg:px-24">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] font-light text-foreground">
            Can there be a different
            <br className="hidden md:block" /> way of living
          </h1>
          <p className="font-accent italic text-xl md:text-2xl text-muted-foreground mt-6 md:mt-8 max-w-2xl mx-auto">
            free from inner conflict, division,
            <br className="hidden md:block" /> and rooted in awareness and love?
          </p>
        </motion.div>
      </section>

      {/* ── Short Introduction ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-2xl mx-auto">
            <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground">
              Bikram Jha is engaged in a lifelong inquiry into human consciousness and the roots of
              suffering. His work is not based on belief, ideology, or spiritual authority, but on
              self inquiry and direct observation of life as it unfolds. Through different initiatives,
              dialogue, programs, and living experiments, he explores whether inner transformation
              can give rise to a different way of living for oneness.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
              data-testid="home-read-more"
            >
              Read More <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* ── Bikram ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">
            <div className="w-48 h-48 md:w-56 md:h-56 flex-shrink-0 rounded-full overflow-hidden" style={{ backgroundColor: "#FDFCF8" }}>
              <img
                src="https://customer-assets.emergentagent.com/job_earth-consciousness/artifacts/x854t9vd_Untitled%20design%20%281%29.png"
                alt="Bikram Jha"
                className="w-full h-full object-cover"
                style={{ mixBlendMode: "multiply" }}
                loading="lazy"
                data-testid="bikram-photo"
              />
            </div>
            <blockquote className="font-accent italic text-xl md:text-2xl text-foreground leading-relaxed text-center md:text-left">
              "Sharing the gift that belongs to all."
              <span className="block font-body not-italic text-sm text-muted-foreground mt-4">
                Bikram
              </span>
            </blockquote>
          </div>
        </FadeIn>
      </section>

      {/* ── Areas of Work ── */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" style={{ backgroundColor: "#F5F5F0" }}>
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-center mb-16 text-foreground">
              Areas of Work
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {workAreas.map((area, i) => (
              <FadeIn key={area.title} delay={i * 0.1}>
                <div
                  className="p-8 border border-[hsl(48,12%,88%)] hover:border-[#5C6B5D]/40 transition-colors duration-300"
                  data-testid={`work-area-${i}`}
                >
                  <h3 className="font-heading text-xl md:text-2xl font-medium mb-4 text-foreground">
                    {area.title}
                  </h3>
                  <p className="font-body text-sm md:text-base leading-relaxed text-muted-foreground">
                    {area.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.4}>
            <div className="text-center mt-12">
              <Link
                to="/the-work"
                className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                data-testid="home-explore-work"
              >
                Explore The Work <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Jeevan Darshan Foundation ── */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground" data-testid="jdf-heading">
              Jeevan Darshan Foundation
            </h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
              A non-profit organization addressing the growing crisis in human consciousness. JDF aims to nurture a new way of living free from suffering, misery, conditioning, and inner slavery, rooted in self-observation, love, peace, inner freedom, clarity, and oneness.
            </p>
            <a
              href="https://jdf.org.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
              data-testid="jdf-link"
            >
              Visit JDF <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </FadeIn>
      </section>

      {/* ── JDF Life Workshop ── */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" style={{ backgroundColor: "#F5F5F0" }}>
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-6 text-foreground" data-testid="life-workshop-heading">
              JDF Life Workshop
            </h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
              The JDF Life Workshop is a space for deep self-inquiry into the fundamental causes of human suffering. It offers an opportunity to observe life as it is, without beliefs, methods, or borrowed answers, allowing clarity, freedom, love, and oneness to naturally flower.
            </p>
            <a
              href="https://workshop.jdf.org.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
              data-testid="life-workshop-link"
            >
              Learn More <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </FadeIn>
      </section>

      {/* ── Upcoming Event ── */}
      {upcomingEvent && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
          <div className="max-w-screen-xl mx-auto">
            <FadeIn>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-center mb-12 text-foreground">
                Upcoming
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link
                to={`/events/${upcomingEvent.slug}`}
                className="block max-w-3xl mx-auto group"
                data-testid="home-upcoming-event"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                  {upcomingEvent.featured_image && (
                    <div className="md:col-span-5 overflow-hidden">
                      <img
                        src={upcomingEvent.featured_image}
                        alt={upcomingEvent.title}
                        className="w-full aspect-[4/3] object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className={upcomingEvent.featured_image ? "md:col-span-7" : "md:col-span-12"}>
                    <Badge variant="outline" className="mb-4 font-body text-xs tracking-wider uppercase border-[#5C6B5D] text-[#5C6B5D]">
                      {upcomingEvent.event_type}
                    </Badge>
                    <h3 className="font-heading text-2xl md:text-3xl font-medium mb-3 text-foreground group-hover:text-[#5C6B5D] transition-colors duration-300">
                      {upcomingEvent.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground font-body mb-3">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={14} strokeWidth={1.5} />
                        {new Date(upcomingEvent.event_date).toLocaleDateString("en-US", {
                          month: "long", day: "numeric", year: "numeric",
                        })}
                      </span>
                      {upcomingEvent.location && (
                        <span className="flex items-center gap-1.5">
                          <MapPin size={14} strokeWidth={1.5} />
                          {upcomingEvent.location}
                        </span>
                      )}
                    </div>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {upcomingEvent.short_description}
                    </p>
                  </div>
                </div>
              </Link>
            </FadeIn>
          </div>
        </section>
      )}

      {/* ── Selected Videos ── */}
      {videos.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" style={{ backgroundColor: "#F5F5F0" }}>
          <div className="max-w-screen-xl mx-auto">
            <FadeIn>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-center mb-12 text-foreground">
                Watch
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, i) => (
                <FadeIn key={video.id} delay={i * 0.1}>
                  <div data-testid={`home-video-${i}`}>
                    <div className="video-embed">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.youtube_video_id}`}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    <h4 className="font-heading text-lg mt-4 text-foreground">{video.title}</h4>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.3}>
              <div className="text-center mt-12">
                <Link
                  to="/watch-read"
                  className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                  data-testid="home-view-all-videos"
                >
                  View All <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* ── Read ── */}
      {journals.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
          <div className="max-w-screen-xl mx-auto">
            <FadeIn>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-center mb-12 text-foreground">
                Read
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-3xl mx-auto">
              {journals.map((j, i) => (
                <FadeIn key={j.id} delay={i * 0.1}>
                  <Link to={`/journal/${j.slug}`} className="group block" data-testid={`home-journal-${i}`}>
                    {j.featured_image && (
                      <div className="overflow-hidden mb-4">
                        <img src={j.featured_image} alt={j.title} className="w-full aspect-[16/9] object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                      </div>
                    )}
                    <h4 className="font-heading text-xl font-medium text-foreground group-hover:text-[#5C6B5D] transition-colors duration-300 mb-2">{j.title}</h4>
                    <p className="font-body text-sm text-muted-foreground line-clamp-2">{j.excerpt}</p>
                  </Link>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.2}>
              <div className="text-center mt-12">
                <Link to="/watch-read" className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300" data-testid="home-view-all-journals">
                  View All Writings <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* ── Invitation ── */}
      <section className="py-32 md:py-40 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight font-light mb-8 text-foreground">
              An Invitation to Inquiry
            </h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-muted-foreground mb-10">
              If you feel the urgency to look at life directly, without escape or comfort in
              ready-made conclusions, you are welcome. Not as a follower, but as a fellow inquirer.
            </p>
            <Link
              to="/dialogue"
              className="inline-block font-body text-sm tracking-[0.15em] uppercase px-8 py-3 border border-[#5C6B5D] text-[#5C6B5D] hover:bg-[#5C6B5D] hover:text-white transition-all duration-300"
              data-testid="home-dialogue-link"
            >
              Begin a Dialogue
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
